/**
 * Badge de priorite pour les tickets
 */

import { AlertTriangle, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TicketPriority } from '../types/tickets.types';

interface TicketPriorityBadgeProps {
  priority: TicketPriority;
  showIcon?: boolean;
  size?: 'sm' | 'md';
}

const priorityStyles: Record<TicketPriority, { className: string; Icon: React.ElementType }> = {
  LOW: {
    className: 'bg-gray-50 text-gray-600',
    Icon: ArrowDown,
  },
  MEDIUM: {
    className: 'bg-blue-50 text-blue-600',
    Icon: ArrowUp,
  },
  HIGH: {
    className: 'bg-orange-50 text-orange-600',
    Icon: AlertTriangle,
  },
  URGENT: {
    className: 'bg-red-50 text-red-600',
    Icon: AlertCircle,
  },
};

const priorityI18nKeys: Record<TicketPriority, string> = {
  LOW: 'support.priorities.LOW',
  MEDIUM: 'support.priorities.MEDIUM',
  HIGH: 'support.priorities.HIGH',
  URGENT: 'support.priorities.URGENT',
};

export function TicketPriorityBadge({
  priority,
  showIcon = true,
  size = 'md',
}: TicketPriorityBadgeProps) {
  const { t } = useTranslation();
  const config = priorityStyles[priority] || priorityStyles.MEDIUM;
  const labelKey = priorityI18nKeys[priority] || priorityI18nKeys.MEDIUM;
  const Icon = config.Icon;

  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';

  return (
    <span
      className={`inline-flex items-center gap-1 font-medium rounded-lg backdrop-blur-sm
                  transition-all duration-200 hover:shadow-sm ${config.className} ${sizeClasses}`}
    >
      {showIcon && <Icon className={iconSize} />}
      {t(labelKey)}
    </span>
  );
}
